import React, {useRef} from 'react';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';

import Button from '../../../components/ui-controls/Button';
import TextInput, {type TextInputRef} from '../../../components/ui-controls/form/TextInput';
import {Paragraph} from '../../../components/ui-controls/text/Paragraph';
import {
  GiftCardEntryFormData,
  giftCardEntrySchema,
} from '../../../models/schemas/giftCardsSchemaZod';

type GiftCardDataInputViewProps = {
  isSubmitting: boolean;
  onSubmit: (number: string, pin: string) => void;
};

const GiftCardDataInputFormView = ({isSubmitting, onSubmit}: GiftCardDataInputViewProps) => {
  const pinInput = useRef<TextInputRef>(null);

  const {
    handleSubmit,
    formState: {errors},
    setValue,
    watch,
  } = useForm<GiftCardEntryFormData>({
    resolver: zodResolver(giftCardEntrySchema),
    defaultValues: {number: '', pin: ''},
    mode: 'onSubmit', // Match original: validateOnChange=false, validateOnBlur=false
  });

  const numberValue = watch('number');
  const pinValue = watch('pin');

  return (
    <>
      <Paragraph>
        Sometimes we lose track of our spending so we have made it easy to check the balance on your
        gift card and show you what you have spent.
        {'\n'}
        {'\n'}
        Simply type your Gift Card number and PIN below and submit.
      </Paragraph>
      <TextInput
        accessibilityLabel="Card number"
        autoFocus={true}
        errorMessage={errors.number?.message}
        keyboardType="numeric"
        label="Card number"
        onChangeText={(text: string) => setValue('number', text)}
        onSubmitEditing={() => pinInput.current?.focus()}
        placeholder="Enter the card number"
        returnKeyType="next"
        testID="gift-card-balance-card-number-input"
        value={numberValue}
      />
      <TextInput
        accessibilityLabel="PIN"
        errorMessage={errors.pin?.message}
        keyboardType="numeric"
        label="PIN"
        onChangeText={(text: string) => setValue('pin', text)}
        onSubmitEditing={handleSubmit(v => onSubmit(v.number, v.pin))}
        placeholder="Enter the PIN"
        ref={pinInput}
        returnKeyType="done"
        testID="gift-card-balance-pin-input"
        value={pinValue}
      />
      <Button
        disabled={isSubmitting}
        loading={isSubmitting}
        onPress={handleSubmit(v => onSubmit(v.number, v.pin))}
        testID="gift-card-balance-submit-button"
        title="SUBMIT"
      />
    </>
  );
};

export default GiftCardDataInputFormView;
